import React, { ReactNode } from "react";
import { Box, Typography, SxProps } from "@mui/material";

type VariantContext = {
  variants: Variants;
  updateVariants: (v: Partial<Variants>) => void;
};
const VariantsContext = React.createContext<VariantContext>({
  variants: { "--controller-width": "50px", "--status-width": "4px" },
  updateVariants: () => {},
});
const useVariants = () => {
  const context = React.useContext<VariantContext>(VariantsContext);
  if (context === undefined) {
    throw new Error("useVariant must be used within a VariantsProvider");
  }
  return context;
};

type Variants = {
  "--controller-width": string;
  "--status-width": string;
};

type RootProps = {
  key: string;
  children: ReactNode;
};

const Root: React.FC<RootProps> = ({ key, children }) => {
  const [variants, setVariants] = React.useState<Variants>({
    "--controller-width": "50px",
    "--status-width": "4px",
  });
  const updateVariants = (newVariants: Partial<Variants>) => {
    setVariants((pre) => ({
      ...pre,
      ...newVariants,
    }));
  };
  const styles = {
    "container--hover": {
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    },
    "container--dragging": {
      opacity: "0.5",
    },
  };
  let controllerChild: ReactNode = null;
  let statusChild: ReactNode = null;
  const propertyChildren: ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type === Status && statusChild === null) {
      statusChild = child;
    }
    if (child.type === Controller && controllerChild === null) {
      controllerChild = child;
    }
    if (child.type === Property) {
      propertyChildren.push(child);
    }
  });
  return (
    <VariantsContext.Provider value={{ variants, updateVariants }}>
      <Box
        key={key}
        sx={{
          ...variants,
          position: "relative",
          borderRadius: "2px",
          padding: "6px 50px 6px 16px",
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "10px 20px",
          overflow: "hidden",
          flexShrink: 0,
          opacity: 1,
          boxShadow: (theme) =>
            theme.palette.mode === "light"
              ? "0 1px 3px rgba(0, 0, 0, 0.08)"
              : "0 1px 3px rgba(255, 255, 255, 0.08)",
          transition: "box-shadow 0.3s",
          "&:hover": {
            boxShadow: (theme) =>
              theme.palette.mode === "light"
                ? "0 4px 8px rgba(0, 0, 0, 0.2)"
                : "0 4px 8px rgba(255, 255, 255, 0.2)",
          },
        }}
      >
        {statusChild}
        {propertyChildren}
        {controllerChild}
      </Box>
    </VariantsContext.Provider>
  );
};

type PropertyProps = {
  label: string;
  sx?: SxProps;
  children: ReactNode;
};
const Property: React.FC<PropertyProps> = ({ label, sx = {}, children }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.9em",
          fontStyle: "italic",
          color: "#adaeb0"
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

type DocStatus = "ERROR" | "SUCCESS" | "PENDING" | "UPLOADING";

type StatusProps = {
  sx?: SxProps;
  width?: string;
  status: DocStatus;
  children?: ReactNode;
};
const Status: React.FC<StatusProps> = ({
  status,
  sx = {},
  width,
  children,
}) => {
  const { updateVariants } = useVariants();
  React.useEffect(() => {
    if (width) {
      updateVariants({
        "--status-width": width,
      });
    }
  }, [width]);
  const bgColors = {
    ERROR: "#ff4d4f",
    SUCCESS: "#276738",
    PENDING: "#faad14",
    UPLOADING: "#faad14",
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "var(--status-width)",
        position: "absolute",
        left: 0,
        top: 0,
        transition: "width 1s ease-in-out",
        backgroundColor: bgColors[status as DocStatus],
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

type ControllerProps = {
  width?: string;
  sx?: SxProps;
  children: ReactNode;
};
const Controller: React.FC<ControllerProps> = ({
  width,
  sx = {},
  children,
}) => {
  const { updateVariants } = useVariants();
  React.useEffect(() => {
    if (width) {
      updateVariants({
        "--controller-width": width,
      });
    }
  }, [width]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: "var(--controller-width)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "6px 0",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const AmlDocument = {
  Root,
  Status,
  Controller,
  Property,
};

export default AmlDocument;
